/**
 * calendar/create-event.js — Single owner of the "attach a quote/job to the calendar"
 * request, and of the one rule that can refuse it.
 *
 * Two surfaces schedule work: the quick-schedule modal (schedule-modal.js) and dropping
 * an item from the unscheduled panel (unscheduled-panel.js). Both need the identical
 * event title, the identical local-wall-clock datetime format, the identical
 * quote_id-vs-job_id branch, and the identical "not in the past" guard. That payload
 * shape is the API contract, so it lives here once — the callers own only their own UI
 * (spinner, modal, panel row) and how they surface the refusal.
 *
 * Datetimes are sent as LOCAL wall clock with no timezone suffix ("2026-07-28T09:00:00"):
 * the server parses with datetime.fromisoformat and localises. Never toISOString() here —
 * that shifts to UTC and lands a 9am booking on the wrong day either side of midnight.
 *
 * Imports `state` (for EVENTS_API) and the shared assignee-picker reader, so it adds no
 * cycle to the import graph.
 */

import { readAssigneeSelection } from '../utils/assignee-selection.js';
import { EVENTS_API } from './state.js';

const pad = (n) => String(n).padStart(2, '0');

/** Refused by both surfaces, so the wording cannot drift between them. */
export const PAST_DAY_MESSAGE = 'Cannot schedule events in the past';

/**
 * True when `date` falls on a day before today.
 *
 * Compared at DAY granularity, not instant: booking 9am for later today is fine, and a
 * time earlier today is not worth blocking. Both operands are flattened to local midnight
 * so the comparison never turns on the time of day the check happens to run at.
 */
export function isPastDay(date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const day = new Date(date);
    day.setHours(0, 0, 0, 0);
    return day < today;
}

/**
 * True when booking `start` would put work in the past: a timed booking earlier than now, or
 * an all-day one on a day before today. An all-day booking is stored from 09:00, so it is
 * judged by its day; a visit later today is fine however early the day began. This is the
 * rule every booking surface asks, and the one the grey "already over" shading depicts.
 */
export function isPastSlot(start, allDay) {
    return allDay ? isPastDay(start) : start < new Date();
}

/**
 * Format a Date as local wall-clock ISO — "YYYY-MM-DDTHH:mm:ss", no zone suffix.
 * Exported for the Dispatch board, whose reschedule PUT sends the same format.
 */
export function toLocalDatetimeStr(d) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
        `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/**
 * The event title shown on every chip. Parsed back apart by core.js's mobileChipLines
 * (it regexes the "#123" out of the title), so the "#<id>" must stay in this shape.
 */
export function eventTitleFor(entityType, entityId, clientName) {
    const label = entityType === 'quote' ? 'Quote' : 'Job';
    return `${label} #${entityId} - ${clientName}`;
}

/**
 * Read a schedulable row's data-* contract (templates/components/schedule_entity_item.html)
 * into the entity half of postCalendarEvent's options. Every surface that books a row
 * reads it through here: the calendar's Unscheduled panel and the Dispatch board.
 */
export function entityFromScheduleRow(el) {
    return {
        entityType: el.dataset.scheduleType,
        entityId: parseInt(el.dataset.scheduleId, 10),
        clientId: parseInt(el.dataset.clientId, 10),
        clientName: el.dataset.clientName,
        location: el.dataset.location || null
    };
}

/**
 * POST a new calendar event for a quote or job.
 *
 * @param {object}  opts
 * @param {string}  opts.entityType  'quote' | 'job'
 * @param {number}  opts.entityId    quote or job id
 * @param {number}  opts.clientId
 * @param {string}  opts.clientName  used to build the title
 * @param {string}  [opts.location]
 * @param {Date}    opts.start
 * @param {Date}    opts.end
 * @param {boolean} [opts.allDay]
 * @param {Element} [opts.crewScope] element containing the assignee picker whose
 *        selection should ride along. Omitted by callers with no picker (the
 *        unscheduled-panel drop), which creates the visit with nobody on it.
 * @param {object}  [opts.crew] an explicit {assignee_ids, lead_user_id}, for a caller
 *        that knows the crew without a picker (a drop on a team member's lane on the
 *        Dispatch board). Wins over crewScope.
 * @returns {Promise<object>} the parsed JSON body on success
 * @throws {Error} with the server's message when the request fails
 */
export async function postCalendarEvent({ entityType, entityId, clientId, clientName, location, start, end, allDay, crewScope, crew }) {
    const body = {
        title: eventTitleFor(entityType, entityId, clientName),
        description: null,
        start_datetime: toLocalDatetimeStr(start),
        end_datetime: toLocalDatetimeStr(end),
        event_type: entityType,
        location: location || null,
        client_id: clientId,
        all_day: !!allDay,
        // Crew keys only when this caller has a picker; readAssigneeSelection
        // returns {} for an absent one, and an absent picker must not be read
        // as "assign nobody explicitly".
        ...(crew || (crewScope ? readAssigneeSelection(crewScope) : {}))
    };
    if (entityType === 'quote') {
        body.quote_id = entityId;
    } else {
        body.job_id = entityId;
    }

    // Plain fetch: the app-wide wrapper stamps the CSRF token on same-origin writes.
    const response = await fetch(EVENTS_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) {
        throw new Error(data.error || data.message || 'Failed to schedule event');
    }
    return data;
}
